import { EnhancementReportStat } from "../../../../models/enhancement/enhancement-report-stats"
import { EnhancementViewLevel } from '../../ReportViewLevelSelector'

const MAX_EMPLOYEE_RESULTS = 20

export function processChartData(data: EnhancementReportStat[], selectedRequestTypes?: string[] | null, viewLevel: EnhancementViewLevel = EnhancementViewLevel.DEPARTMENT) {
  if (!data?.length) {
    return {
      departments: [],
      submittedEnhancements: [],
      overwrittenEnhancements: [],
      overwrittenPercentages: []
    }
  }
  
  const filteredData = selectedRequestTypes && selectedRequestTypes.length > 0
    ? data.filter(item => selectedRequestTypes.includes(item.requestType))
    : data
  
  const employeeStats = viewLevel === EnhancementViewLevel.EMPLOYEE
    ? filteredData.reduce((acc, stat) => {
      if (!stat.employee) return acc 
      if (!acc[stat.employee]) {
        acc[stat.employee] = {
          count: 0,
          departments: new Set<string>()
        }
      }
      acc[stat.employee].count += 1
      acc[stat.employee].departments.add(stat.department)
      return acc
    }, {} as Record<string, { count: number; departments: Set<string> }>)
    : {}

  const groupingKey = (stat: EnhancementReportStat): string => {
    switch (viewLevel) {
      case EnhancementViewLevel.TEAM:
        return stat.team || 'No Team'
      case EnhancementViewLevel.EMPLOYEE: {
        if (!stat.employee) return 'No Employee'
        const stats = employeeStats[stat.employee]
        if (stats && stats.count > 1 && stats.departments.size > 1) {
          return `${stat.employee} (${stat.department})`
        }
        return stat.employee
      }
      case EnhancementViewLevel.DEPARTMENT:
      default:
        return stat.department
    }
  }

  const groupMap = filteredData.reduce((acc, stat) => {
    const key = groupingKey(stat)

    if (!acc.has(key)) {
      acc.set(key, {submitted: 0, overwritten: 0})
    }

    const groupData = acc.get(key)!

    groupData.submitted += 1

    if (stat.originalPaidHours !== stat.finalPaidHours) {
      groupData.overwritten += 1
    }

    return acc
  }, new Map<string, { submitted: number, overwritten: number }>())
  
  const filteredGroups = Array.from(groupMap.entries())
    .filter(([_, value]) => value.submitted > 0)
    .sort((a, b) => b[1].submitted - a[1].submitted)

  const finalGroups = viewLevel === EnhancementViewLevel.EMPLOYEE
    ? filteredGroups.slice(0, MAX_EMPLOYEE_RESULTS)
    : filteredGroups

  return finalGroups.reduce((result, [groupName, value]) => {
    result.departments.push(groupName)
    result.submittedEnhancements.push(value.submitted)
    result.overwrittenEnhancements.push(value.overwritten)

    const percentage = value.submitted > 0
      ? parseFloat(((value.overwritten / value.submitted) * 100).toFixed(2))
      : 0
    result.overwrittenPercentages.push(percentage)

    return result
  }, {
    departments: [] as string[],
    submittedEnhancements: [] as number[],
    overwrittenEnhancements: [] as number[],
    overwrittenPercentages: [] as number[]
  })
}